import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { CREDIBILITY, SEGMENTS, ASSET_CLASSES, PRODUCTS, INSIGHTS, MANIFESTO, COMPANY } from "@/data/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1515703944563-dbcfbf121b3d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwZGFyayUyMG1vb2R5fGVufDB8fHx8MTc4MzMwMzQzMXww&ixlib=rb-4.1.0&q=85";
const SERVER_IMG =
  "https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxzZXJ2ZXIlMjByb29tJTIwbGlnaHRzJTIwZGFya3xlbnwwfHx8fDE3ODMzMDM0MzF8MA&ixlib=rb-4.1.0&q=85";

const HERO_LINES = ["Liquidity, connectivity", "and analytics for", "global markets."];

const lineContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const lineChild = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
};

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0D12] via-[#0A0D12]/90 to-[#0A0D12]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D12] via-transparent to-[#0A0D12]/60" />
        </motion.div>

        <motion.div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-28 pb-16" style={{ y: contentY, opacity: contentOpacity }}>
          <div className="max-w-3xl">
            <motion.div
              className="flex items-center gap-4 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.05 }}
            >
              <motion.span
                className="h-px bg-[#C8A45A]"
                initial={{ width: 0 }}
                animate={{ width: 40 }}
                transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              />
              <span className="overline">Wholesale Prime of Prime · Australia</span>
            </motion.div>

            <motion.h1
              className="font-display text-4xl sm:text-5xl lg:text-[4.2rem] font-light leading-[1.05] tracking-tight text-white"
              variants={lineContainer}
              initial="hidden"
              animate="show"
            >
              {HERO_LINES.map((line, i) => (
                <span key={i} className="reveal-line">
                  <motion.span className="block" variants={lineChild}>
                    {i === 1 ? (
                      <>and <span className="gold-text font-normal">analytics</span> for</>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </motion.h1>

            <motion.p
              className="mt-8 text-lg text-[#A1A5AB] leading-relaxed max-w-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              {COMPANY.shortName} is an Australian wholesale prime-of-prime. Once our Australian financial
              services licence is granted, we will provide multi-asset CFD liquidity, execution technology and
              real-time risk analytics to brokers, funds and professional desks — under a single Australian counterparty.
            </motion.p>
            <motion.div
              className="mt-10 flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                to="/contact"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center justify-center gap-2 bg-[#C8A45A] text-[#0A0D12] hover:bg-[#D4B572] transition-colors px-8 py-4 text-sm font-medium tracking-wide"
              >
                Start a conversation
                <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/solutions"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white hover:border-[#C8A45A] hover:text-[#C8A45A] transition-colors px-8 py-4 text-sm tracking-wide"
              >
                Explore our solutions
              </Link>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          className="absolute bottom-8 left-0 right-0 z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
            <span className="text-xs text-[#737882] font-mono">{COMPANY.clientNotice}</span>
            <span className="hidden md:flex items-center gap-2 text-xs text-[#737882] font-mono">
              <span className="w-px h-8 bg-white/20 animate-pulse" /> Scroll
            </span>
          </div>
        </motion.div>
      </section>

      {/* EDITORIAL MARQUEE */}
      <Marquee items={["Liquidity", "Connectivity", "Analytics", "Execution", "Foreign Exchange", "Metals", "Indices", "Wholesale"]} testId="home-marquee" />

      {/* CREDIBILITY BAR */}
      <section className="border-y border-white/10 bg-[#0A0D12] relative z-10" data-testid="credibility-bar">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
            {CREDIBILITY.map((item, i) => (
              <Reveal key={item.label} delay={i * 0.08} className="py-10 px-4 lg:px-8 first:pl-0">
                <div className="font-display text-2xl md:text-3xl font-light text-white mb-2">{item.value}</div>
                <div className="text-xs text-[#737882] uppercase tracking-wider">{item.label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT SEGMENTS */}
      <section className="py-24 md:py-32 relative z-10" data-testid="segments-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <span className="overline">Who we serve</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white mt-5 max-w-2xl">
              Built around your business, not a product sheet.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16">
            {SEGMENTS.map((seg, i) => (
              <Reveal key={seg.id} delay={i * 0.08}>
                <Link
                  to="/solutions"
                  data-testid={`segment-card-${seg.id}`}
                  className="group block h-full bg-[#11151C]/60 backdrop-blur-sm border border-white/10 hover:border-[#C8A45A]/50 transition-all p-8 md:p-10"
                >
                  <div className="flex items-start justify-between">
                    <span className="font-mono text-xs text-[#737882]">0{i + 1}</span>
                    <ArrowUpRight size={18} className="text-[#737882] group-hover:text-[#C8A45A] transition-colors" />
                  </div>
                  <h3 className="font-display text-2xl font-medium text-white mt-8 group-hover:text-[#C8A45A] transition-colors">
                    {seg.title}
                  </h3>
                  <p className="text-[#A1A5AB] mt-3 leading-relaxed">{seg.line}</p>
                  <p className="text-sm text-[#737882] mt-5 leading-relaxed border-t border-white/5 pt-5">
                    {seg.detail}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENTIATOR */}
      <section className="py-24 md:py-32 border-y border-white/10 relative z-10 grid-lines" data-testid="differentiator-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <span className="overline">The ET Prime difference</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white mt-6 leading-tight">
              A wholesale prime-of-prime built to <span className="gold-text">price flow</span>, not just pass it through.
            </h2>
            <p className="mt-8 text-lg text-[#A1A5AB] leading-relaxed max-w-3xl mx-auto">
              Most intermediaries only route. ET Prime is being built as a wholesale prime-of-prime that will make
              markets and internalise flow where it improves your execution, aggregate third-party liquidity
              where it doesn't, and act as a single Australian counterparty across every asset class — once our
              Australian financial services licence, currently in draft, is granted.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="py-24 md:py-32 relative z-10" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <span className="overline">Our principles</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white mt-5 max-w-2xl">
              Three convictions we price into everything.
            </h2>
          </Reveal>
          <div className="mt-16 border-t border-white/10">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.06}>
                <div className="group grid grid-cols-1 md:grid-cols-12 gap-6 py-10 border-b border-white/10 transition-colors hover:bg-[#11151C]/40" data-testid={`manifesto-${m.n}`}>
                  <div className="md:col-span-2">
                    <span className="font-display text-5xl md:text-6xl font-extralight text-[#C8A45A]/30 group-hover:text-[#C8A45A]/70 transition-colors">{m.n}</span>
                  </div>
                  <h3 className="md:col-span-5 font-display text-2xl md:text-3xl font-light text-white leading-tight">{m.title}</h3>
                  <p className="md:col-span-5 text-[#A1A5AB] leading-relaxed md:pt-2">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ASSET CLASSES */}
      <section className="py-24 md:py-32 relative z-10" data-testid="assets-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            <Reveal className="lg:col-span-1">
              <span className="overline">Coverage</span>
              <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-white mt-5">
                One relationship. Every market.
              </h2>
              <p className="text-[#A1A5AB] mt-6 leading-relaxed">
                Cross-margined CFD exposure across the global asset classes your desk trades — settled and
                reported through a single institutional relationship.
              </p>
              <Link
                to="/solutions"
                data-testid="assets-link"
                className="inline-flex items-center gap-2 text-[#C8A45A] hover:text-[#D4B572] transition-colors mt-8 text-sm tracking-wide link-underline"
              >
                View execution models <ArrowRight size={16} />
              </Link>
            </Reveal>

            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {ASSET_CLASSES.map((a, i) => (
                <Reveal key={a.name} delay={i * 0.05} className="bg-[#0A0D12] p-8 hover:bg-[#11151C] transition-colors">
                  <div className="font-display text-xl text-white">{a.name}</div>
                  <div className="text-sm text-[#737882] mt-2">{a.note}</div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORM TEASER */}
      <section className="relative z-10 overflow-hidden" data-testid="platform-teaser">
        <div className="absolute inset-0">
          <img src={SERVER_IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-[#0A0D12]/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="overline">The platform</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white mt-5 max-w-2xl">
              Vital tools built for the desks we will work with.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.1}>
                <div className="border border-white/10 bg-[#0A0D12]/60 backdrop-blur-sm p-8 h-full">
                  <div className="font-mono text-xs uppercase tracking-widest text-[#C8A45A]">{p.kind}</div>
                  <div className="font-display text-3xl text-white mt-4">{p.name}</div>
                  <p className="text-[#A1A5AB] mt-4 leading-relaxed">{p.tagline}</p>
                  <p className="text-sm text-[#737882] mt-5 border-t border-white/5 pt-5 leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/technology"
              data-testid="infra-link"
              className="inline-flex items-center gap-2 text-[#C8A45A] hover:text-[#D4B572] transition-colors mt-12 text-sm tracking-wide link-underline"
            >
              Explore our technology <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* INSIGHTS TEASER */}
      <section className="py-24 md:py-32 relative z-10 border-t border-white/10" data-testid="insights-teaser">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <span className="overline">Insights</span>
              <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-white mt-5">
                Market structure, from the desk.
              </h2>
            </div>
            <Link
              to="/insights"
              data-testid="insights-view-all"
              className="inline-flex items-center gap-2 text-[#A1A5AB] hover:text-white transition-colors text-sm"
            >
              View all insights <ArrowRight size={16} />
            </Link>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-16">
            {INSIGHTS.map((post, i) => (
              <Reveal key={post.id} delay={i * 0.08} className="bg-[#0A0D12]">
                <Link to="/insights" data-testid={`insight-teaser-${post.id}`} className="group block p-8 h-full hover:bg-[#11151C] transition-colors">
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-[#C8A45A] font-mono uppercase tracking-widest">{post.category}</span>
                    <span className="text-[#737882]">·</span>
                    <span className="text-[#737882]">{post.date}</span>
                  </div>
                  <h3 className="font-display text-xl text-white mt-6 leading-snug group-hover:text-[#C8A45A] transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-sm text-[#737882] mt-4 leading-relaxed">{post.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <CtaBand />
    </div>
  );
}

export const CtaBand = () => (
  <section className="py-24 md:py-32 relative z-10 border-t border-white/10 bg-[#11151C]" data-testid="cta-band">
    <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
      <Reveal>
        <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight text-white leading-tight">
          Let's talk about your flow.
        </h2>
        <p className="mt-6 text-lg text-[#A1A5AB] max-w-2xl mx-auto">
          Every ET Prime relationship begins with a conversation about your book, your markets and the
          counterparty terms that would move your business forward.
        </p>
        <Link
          to="/contact"
          data-testid="cta-band-button"
          className="group inline-flex items-center gap-2 bg-[#C8A45A] text-[#0A0D12] hover:bg-[#D4B572] transition-colors px-9 py-4 text-sm font-medium tracking-wide mt-10"
        >
          Start a conversation
          <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </Reveal>
    </div>
  </section>
);
